import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bell, Radar, RefreshCw, Mail, X, Loader2, ExternalLink, Sparkles,
  Filter, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import EmergingSignalsTab from './EmergingSignalsTab';

const API = '/api';

const STAGE_CHIP = {
  'Pre-Clinical': 'bg-slate-100 text-slate-700',
  'Phase 1': 'bg-sky-100 text-sky-700',
  'Phase 2': 'bg-indigo-100 text-indigo-700',
  'Phase 3': 'bg-violet-100 text-violet-700',
  'Approved': 'bg-emerald-100 text-emerald-700',
  'Commercial': 'bg-emerald-100 text-emerald-700',
};

function AssetAlertsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [actionBusy, setActionBusy] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending'); // pending | emailed | dismissed
  const navigate = useNavigate();

  const fetchAlerts = async (status = statusFilter) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/alerts?status=${status}`, { withCredentials: true });
      setGroups(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleRescan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/alerts/scan-all`, {}, { withCredentials: true, timeout: 120000 });
      const { alerts_created, contacts_scanned, assets_scanned } = res.data;
      if (alerts_created > 0) {
        toast.success(`Found ${alerts_created} new alert${alerts_created !== 1 ? 's' : ''} across ${contacts_scanned} contacts (${assets_scanned} active-partner assets scanned)`);
      } else {
        toast(`Scanned ${assets_scanned} active-partner assets — no new matches`, { icon: 'ℹ️' });
      }
      fetchAlerts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleDismiss = async (alertId) => {
    setActionBusy(prev => ({ ...prev, [alertId]: 'dismissing' }));
    try {
      await axios.post(`${API}/alerts/${alertId}/dismiss`, {}, { withCredentials: true });
      // Optimistically remove from groups
      setGroups(prev => prev
        .map(g => ({ ...g, alerts: g.alerts.filter(a => a.id !== alertId) }))
        .filter(g => g.alerts.length > 0)
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setActionBusy(prev => ({ ...prev, [alertId]: null }));
    }
  };

  const handleDismissAll = async (contactId) => {
    setActionBusy(prev => ({ ...prev, [`group-${contactId}`]: 'dismissing' }));
    try {
      const res = await axios.post(`${API}/alerts/contact/${contactId}/dismiss-all`, {}, { withCredentials: true });
      toast.success(`Dismissed ${res.data?.dismissed || 0} alert${res.data?.dismissed === 1 ? '' : 's'}`);
      setGroups(prev => prev.filter(g => g.pharma_contact_id !== contactId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to dismiss group');
    } finally {
      setActionBusy(prev => ({ ...prev, [`group-${contactId}`]: null }));
    }
  };

  const handlePitchTa = async (group, taBucket) => {
    const assetIds = taBucket.alerts.map(a => a.asset_id).filter(Boolean);
    const key = `${group.pharma_contact_id}-${taBucket.ta}`;
    setActionBusy(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(
        `${API}/alerts/contact/${group.pharma_contact_id}/promote-to-matches`,
        { asset_ids: assetIds },
        { withCredentials: true }
      );
      navigate(
        `/pharma/${group.pharma_contact_id}?openPitchPartnersForAlerts=${encodeURIComponent(assetIds.join(','))}&taContext=${encodeURIComponent(taBucket.ta)}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to prepare pitch');
    } finally {
      setActionBusy(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleReachOutBiotechs = (group, ta) => {
    const params = new URLSearchParams({ autoMatchNonPartners: '1' });
    if (ta) params.set('ta', ta);
    navigate(`/pharma/${group.pharma_contact_id}?${params}`);
  };

  const totalAlerts = groups.reduce((sum, g) => sum + g.alerts.length, 0);

  const STATUS_TABS = [
    { key: 'pending', label: 'Pending Action', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    { key: 'emailed', label: 'Emailed', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { key: 'dismissed', label: 'Dismissed', color: 'bg-slate-100 text-slate-600 border-slate-300' },
  ];

  // Card styling depends on status filter — green for emailed, amber for
  // pending, slate for dismissed
  const cardClasses = statusFilter === 'emailed'
    ? 'border border-emerald-200 bg-emerald-50/30'
    : statusFilter === 'dismissed'
    ? 'border border-slate-200 bg-slate-50/40'
    : 'border border-amber-200 bg-amber-50/40';

  return (
    <div data-testid="asset-alerts-tab">
      <div className="flex items-start justify-between gap-4 mb-5">
        <p className="text-sm text-slate-500 max-w-2xl">
          When a new active-partner asset matches a pharma contact's stated TAs, an alert appears here. Each contact's matches are bucketed by therapeutic area so you can pitch <i>one TA at a time</i> — keeping the email short. Use <span className="font-medium text-emerald-700">Pitch to [Name]</span> for active partners (one consolidated email to the pharma) or <span className="font-medium text-sky-700">Reach DB biotechs</span> to email each non-partner biotech CEO an intro pitch.
        </p>
        <Button
          onClick={handleRescan}
          disabled={scanning}
          className="bg-sky-600 text-white hover:bg-sky-700 shrink-0 h-9"
          data-testid="rescan-alerts-btn"
        >
          {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-scanning…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-scan All Assets</>}
        </Button>
      </div>

      {/* Status filter pills */}
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="alerts-status-filter">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold mr-2">Status:</span>
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            className={`text-xs px-3 py-1 rounded-full transition-all border ${statusFilter === t.key ? t.color + ' font-semibold shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
            data-testid={`alerts-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : groups.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-12 text-center">
            <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">
              {statusFilter === 'pending' && 'No pending alerts'}
              {statusFilter === 'emailed' && 'No emailed alerts yet'}
              {statusFilter === 'dismissed' && 'No dismissed alerts'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {statusFilter === 'pending' && "When new active-partner assets are added, matches against your contacts' stated TAs will appear here."}
              {statusFilter === 'emailed' && 'Send an email from a Pending alert and it will move here for the audit trail.'}
              {statusFilter === 'dismissed' && 'Anything you mark "Dismiss" will appear here.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-3">
            <span className="font-semibold text-slate-700">{totalAlerts}</span> pending alert{totalAlerts === 1 ? '' : 's'} across <span className="font-semibold text-slate-700">{groups.length}</span> contact{groups.length === 1 ? '' : 's'}
          </div>
          <div className="space-y-4">
            {groups.map(g => (
              <Card key={g.pharma_contact_id} className={cardClasses} data-testid={`alert-group-${g.pharma_contact_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pharma interest from:</span>
                        <a
                          href={`/pharma/${g.pharma_contact_id}`}
                          className="text-sm font-bold text-slate-900 hover:underline flex items-center gap-1"
                        >
                          {g.pharma_contact_name}
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                        <span className="text-xs text-slate-500">·</span>
                        <span className="text-xs text-slate-600">{g.pharma_company_name}</span>
                        <span className="text-xs text-slate-500">·</span>
                        <span className="text-[11px] text-slate-600">
                          <span className="font-semibold">{g.alerts.length}</span> assets across <span className="font-semibold">{g.total_companies}</span> partner companies
                        </span>
                        {statusFilter === 'emailed' && g.last_emailed_at && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] flex items-center gap-1">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            Last pitched {new Date(g.last_emailed_at).toLocaleDateString()}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {statusFilter === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDismissAll(g.pharma_contact_id)}
                        disabled={!!actionBusy[`group-${g.pharma_contact_id}`]}
                        className="h-8 text-xs text-slate-500 hover:text-rose-600 shrink-0"
                      >
                        Dismiss all
                      </Button>
                    )}
                    {statusFilter === 'emailed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/pharma/${g.pharma_contact_id}`)}
                        className="h-8 text-xs shrink-0"
                      >
                        View contact
                      </Button>
                    )}
                  </div>

                  {/* TA buckets — one row per matched parent TA */}
                  <div className="space-y-2">
                    {(g.ta_buckets || []).map(bucket => {
                      const busyKey = `${g.pharma_contact_id}-${bucket.ta}`;
                      const recentAssets = bucket.alerts.slice(0, 3).map(a => a.asset_name).filter(Boolean);
                      return (
                        <div
                          key={bucket.ta}
                          className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-md px-3 py-2"
                          data-testid={`ta-bucket-${g.pharma_contact_id}-${bucket.ta}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-sky-100 text-sky-800 text-[11px] font-semibold">{bucket.ta}</Badge>
                              <span className="text-[11px] text-slate-600">
                                <span className="font-semibold">{bucket.asset_count}</span> assets · <span className="font-semibold">{bucket.company_count}</span> companies
                              </span>
                            </div>
                            {recentAssets.length > 0 && (
                              <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                                e.g., {recentAssets.join(', ')}{bucket.alerts.length > 3 ? `, +${bucket.alerts.length - 3} more` : ''}
                              </div>
                            )}
                          </div>
                          {statusFilter === 'pending' && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              <Button
                                size="sm"
                                onClick={() => handlePitchTa(g, bucket)}
                                disabled={!!actionBusy[busyKey]}
                                className="bg-emerald-600 text-white hover:bg-emerald-700 h-7 text-[11px]"
                                data-testid={`pitch-ta-${g.pharma_contact_id}-${bucket.ta}`}
                                title={`Send a single consolidated pitch to ${g.pharma_contact_name} listing ${bucket.asset_count} ${bucket.ta} asset${bucket.asset_count === 1 ? '' : 's'}`}
                              >
                                {actionBusy[busyKey]
                                  ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  : <Mail className="w-3 h-3 mr-1" />}
                                Pitch to {g.pharma_contact_name.split(' ')[0]}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReachOutBiotechs(g, bucket.ta)}
                                className="h-7 text-[11px] border-sky-300 text-sky-700 hover:bg-sky-50"
                                data-testid={`reach-biotechs-${g.pharma_contact_id}-${bucket.ta}`}
                                title={`Match database biotechs in ${bucket.ta} and email each biotech CEO an intro pitch`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Reach DB biotechs
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AlertsPage() {
  const [tab, setTab] = useState('alerts');
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="alerts-page">
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-5 h-5 text-sky-600" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Alerts
          </h1>
        </div>
        <p className="text-xs text-slate-500">
          Stay on top of new asset–pharma matches and emerging interest cues from inbound replies.
        </p>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="alerts-tab-bar">
        <button
          onClick={() => setTab('alerts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'alerts'
              ? 'border-sky-600 text-sky-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-asset-alerts"
        >
          <span className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Asset Alerts
          </span>
        </button>
        <button
          onClick={() => setTab('signals')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'signals'
              ? 'border-amber-600 text-amber-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-emerging-signals"
        >
          <span className="flex items-center gap-1.5">
            <Radar className="w-3.5 h-3.5" />
            Emerging Signals
          </span>
        </button>
      </div>

      {tab === 'alerts' ? <AssetAlertsTab /> : <EmergingSignalsTab />}
    </div>
  );
}
